/**
 * The Cars selector above the price table of DOR, LOR and Rank Analyzer (CU-869fmpqfm): cars of one kind at a time —
 * Acriss codes, Categories or Acriss Groups, one or several (v1.2, Curro 2026-10-28) — or All cars with a notice that
 * the cell mixes them. The server intersects the union of the picks with the band's Acriss / Categories
 * (`carPickType` + `carPick`, never the band's keys). The values follow the BI dashboards' rules (`carKinds.js`:
 * `pickCars`, sorted, unique, at most 100).
 *
 * A pick is `{ kind, values, labels }`; `labels` name the values (a group by its name). The single-value shape of
 * v1.1, `{ kind, value, label }`, is still read.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { CARS, CARS_LABEL, DEFAULT_CARS_TAB, carsLabels, carsOn, pickCars } from '../../../@components/KindPillsField/carKinds.js'
import { shortList } from '../../../@components/KindPillsField/kindPills.js'

export const ALL_CARS = Object.freeze({ kind: CARS.ALL, values: [], labels: [] })

// what the server calls each kind
const PICK_TYPE = Object.freeze({ [CARS.ACRISS]: 'acriss', [CARS.CATEGORY]: 'category', [CARS.GROUP]: 'acriss_group' })
// how many picked names a line shows before "+N", and how many codes of the picked groups
const NAMED = 3
const CODES_NAMED = 6

const present = v => v != null && v !== ''

/** The picked values, whatever the shape (v1.1 had one `value`) */
const valuesOf = pick => (Array.isArray(pick?.values) ? pick.values : present(pick?.value) ? [pick.value] : []).filter(present)

export const pickOn = pick => !!pick && pick.kind !== CARS.ALL && !!PICK_TYPE[pick.kind] && valuesOf(pick).length > 0

/** What each picked value is called: the code, the category, the group's name (`#id` until it is known) */
export function pickLabels(pick) {
	const values = valuesOf(pick)
	const labels = Array.isArray(pick?.labels) ? pick.labels : present(pick?.label) ? [pick.label] : []
	return values.map((v, i) => (present(labels[i]) ? String(labels[i]) : pick.kind === CARS.GROUP ? `#${v}` : String(v)))
}

/** The two body keys of the analyzers' request and of the decisions read, or nothing for All */
export function carPickBody(pick) {
	if (!pickOn(pick)) return {}
	return { carPickType: PICK_TYPE[pick.kind], carPick: [...valuesOf(pick)] }
}

/** `Acriss CDMR` · `Acriss CDMR, EDMR` · `Category Economy` · `Group Compact, Vans`; `short` cuts at three with `+N` */
export function pickLabel(pick, { short = false } = {}) {
	if (!pickOn(pick)) return 'All cars'
	const labels = pickLabels(pick)
	const word = pick.kind === CARS.GROUP ? 'Group' : CARS_LABEL[pick.kind]
	return `${word} ${short ? shortList(labels, NAMED) : labels.join(', ')}`
}

/** The pill the menu opens on: the kind in use, or Acriss */
export const pickTab = pick => (pickOn(pick) ? pick.kind : DEFAULT_CARS_TAB)

/**
 * Picking `ids` on the pill `tab` (the field's multi-select): the BI dashboards' rule (`pickCars`) — sorted, unique,
 * another kind replaces the current one, none left is All. `groupOptions` name the groups.
 */
export function nextPick(pick, tab, ids, groupOptions = []) {
	const view = pickOn(pick) ? { cars: pick.kind, carsIds: valuesOf(pick) } : { cars: CARS.ALL, carsIds: [] }
	const next = pickCars(view, tab, ids || [])
	if (!carsOn(next)) return ALL_CARS
	if (next.cars === view.cars && next.carsIds.join('|') === view.carsIds.join('|')) return pick
	return { kind: next.cars, values: next.carsIds, labels: carsLabels(next, { options: groupOptions }) }
}

/**
 * The one line beside the field (UX Obs. 4). With All: "Minimum across N categories — pick a car to compare like
 * with like." (N = 1 → Acriss codes; one car or nothing known → no line). With a pick: what each cell is ("among"
 * with several, the names cut at three with "+N" and the whole list in `title`); groups add their codes as the server
 * resolved them. Rank Analyzer is always the minimum.
 */
export function cellLine({ pick, resolved = null, tab, mix = null, isPosition = false }) {
	const max = !isPosition && tab === 'max'
	if (!pickOn(pick)) {
		if (!mix) return null
		const word = max ? 'Maximum' : 'Minimum'
		const across = mix.categories > 1 ? `${mix.categories} categories` : mix.acriss > 1 ? `${mix.acriss} Acriss codes` : null
		if (!across) return null
		return { tone: 'notice', text: `${word} across ${across} — `, action: 'pick a car', tail: ' to compare like with like.' }
	}
	const several = valuesOf(pick).length > 1
	const codes = pick.kind === CARS.GROUP && resolved?.codes?.length ? resolved.codes : []
	const say = short => `Each cell: the ${max ? 'highest' : 'lowest'} price ${several ? 'among' : 'of'} ${pickLabel(pick, { short })}`
		+ (codes.length ? ` = ${short ? shortList(codes, CODES_NAMED) : codes.join(', ')}` : '')
	const line = { tone: 'info', text: say(true) }
	// the whole list on hover when the line names several cars (or cuts the codes)
	return several || codes.length > CODES_NAMED ? { ...line, title: say(false) } : line
}

const listOf = value => (Array.isArray(value) ? value : value == null || value === '' ? [] : [value]).filter(v => v != null && v !== '')

/** The empty table for a pick (decision 2): says why, naming the band's car filters when they narrow the cars */
export function emptyText({ pick, band = {} }) {
	const label = pickLabel(pick, { short: true })
	const parts = []
	const categories = listOf(band.categories)
	const acriss = listOf(band.acriss)
	if (categories.length) parts.push(`Categories: ${categories.join(', ')}`)
	if (acriss.length) parts.push(`Acriss: ${acriss.join(', ')}`)
	return {
		title: `No prices for ${label} with these filters`,
		text: parts.length
			? `Your filters already narrow the cars to ${parts.join(' and ')}, and ${label} has no prices within them.`
			: `There are no prices for ${label} with the filters of this search.`,
	}
}

/** A group none of whose codes has prices in this execution is shown disabled with its reason (UX Obs. 7) */
export function groupState(group, acrissInExecution) {
	if (!Array.isArray(acrissInExecution)) return { disabled: false, reason: null }
	const inExecution = new Set(acrissInExecution.map(String))
	const any = (group?.acriss || []).some(code => inExecution.has(String(code)))
	return any ? { disabled: false, reason: null } : { disabled: true, reason: 'no prices in this execution' }
}
